import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Sparkles } from "lucide-react";
import Header from "../components/HomePage/header";
import Footer from "../components/HomePage/footer";
import NewsletterPopup from "../Account/NewsletterPopup";

const formatDate = (dateString) => {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "No date";

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
};

const getPostImage = (post) =>
  post?.featuredImage
    ? `${process.env.REACT_APP_DO_SPACES_URL}${post.featuredImage}`
    : "/placeholder.jpg";

const LatestPostsPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchLatestPosts = async () => {
      try {
        const queryParams = new URLSearchParams({
          status: "published",
          sortBy: "createdAt",
          order: "desc",
          limit: 24,
          page: 1,
        }).toString();

        const response = await fetch(`/api/posts?${queryParams}`);
        if (!response.ok) {
          throw new Error(`Failed to fetch posts: ${response.statusText}`);
        }

        const data = await response.json();
        setPosts(data.posts || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchLatestPosts();
  }, []);

  return (
    <>
      <Helmet>
        <title>Latest PTE Blog Posts | PrepNexis</title>
        <meta
          name="description"
          content="Read the latest PrepNexis PTE preparation guides, product updates, and AI-powered study insights."
        />
      </Helmet>
      <Header />
      <main className="min-h-screen bg-slate-50">
        <section className="border-b border-slate-200 bg-slate-50 px-4 py-8 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <nav className="mb-5 flex items-center gap-2 text-sm font-medium text-gray-500">
              <a href="/" className="hover:text-[rgb(126,34,206)]">
                Home
              </a>
              <span className="text-slate-300">/</span>
              <a href="/blog" className="hover:text-[rgb(126,34,206)]">
                Blog
              </a>
              <span className="text-slate-300">/</span>
              <span className="text-[rgb(126,34,206)]">Latest</span>
            </nav>

            <div className="rounded-md border border-slate-200 bg-white p-5 shadow-sm sm:p-7">
              <span className="inline-flex rounded-sm bg-[rgba(126,34,206,0.08)] px-3 py-1 text-xs font-semibold uppercase text-[rgb(126,34,206)] ring-1 ring-[rgba(126,34,206,0.16)]">
                Latest posts
              </span>
              <div className="mt-4 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
                <div>
                  <h1 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
                    Fresh PTE insights from PrepNexis
                  </h1>
                  <p className="mt-3 max-w-3xl text-base leading-7 text-gray-600">
                    Browse the newest strategies, platform updates, and
                    AI-powered study ideas for smarter PTE preparation.
                  </p>
                </div>
                <a
                  href="/signup"
                  className="inline-flex w-fit items-center gap-2 rounded-sm bg-[rgba(126,34,206,0.08)] px-4 py-2.5 text-sm font-semibold text-[rgb(126,34,206)] ring-1 ring-[rgba(126,34,206,0.16)] transition hover:bg-[rgba(126,34,206,0.12)]"
                >
                  <Sparkles className="h-4 w-4" aria-hidden="true" />
                  Get Free Trial
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          {loading && (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3, 4, 5, 6].map((item) => (
                <div
                  key={item}
                  className="min-h-[320px] rounded-md border border-slate-200 bg-white p-4 shadow-sm"
                >
                  <div className="h-40 rounded-md bg-slate-100" />
                  <div className="mt-5 h-4 w-28 rounded-sm bg-slate-100" />
                  <div className="mt-3 h-7 w-full rounded-sm bg-slate-200" />
                  <div className="mt-3 h-4 w-4/5 rounded-sm bg-slate-100" />
                </div>
              ))}
            </div>
          )}

          {error && (
            <div className="rounded-md border border-red-100 bg-white p-5 text-sm font-medium text-red-600 shadow-sm">
              {error}
            </div>
          )}

          {!loading && !error && posts.length === 0 && (
            <div className="rounded-md border border-dashed border-slate-200 bg-white p-10 text-center text-sm font-medium text-gray-500">
              No latest posts available at the moment.
            </div>
          )}

          {!loading && !error && posts.length > 0 && (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {posts.map((post) => (
                <article
                  key={post._id || post.link || post.title}
                  className="group overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:border-[rgba(126,34,206,0.28)] hover:shadow-md"
                >
                  <a href={post.link} className="block overflow-hidden bg-slate-100">
                    <img
                      src={getPostImage(post)}
                      alt={post.title || "Post image"}
                      className="h-52 w-full object-cover transition duration-500 group-hover:scale-105"
                    />
                  </a>
                  <div className="p-5">
                    <div className="flex flex-wrap items-center gap-2 text-sm font-medium text-gray-500">
                      <span>{post.author || "PrepNexis"}</span>
                      <span className="text-slate-300">/</span>
                      <span>{formatDate(post.date)}</span>
                      <span className="text-slate-300">/</span>
                      <span>{post.readMins || "N/A"} mins read</span>
                    </div>
                    <h2 className="mt-3 line-clamp-2 text-xl font-extrabold leading-7 text-gray-900">
                      <a
                        href={post.link}
                        className="hover:text-[rgb(126,34,206)]"
                      >
                        {post.title || "Untitled Post"}
                      </a>
                    </h2>
                    <p className="mt-3 line-clamp-3 text-sm leading-6 text-gray-600">
                      {post.excerpt || "No excerpt available."}
                    </p>
                    <a
                      href={post.link}
                      className="mt-5 inline-flex rounded-sm bg-[rgba(126,34,206,0.08)] px-3 py-1.5 text-sm font-semibold text-[rgb(126,34,206)] ring-1 ring-[rgba(126,34,206,0.16)] transition hover:bg-[rgba(126,34,206,0.12)]"
                    >
                      Read article
                    </a>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </main>
      <NewsletterPopup />
      <Footer />
    </>
  );
};

export default LatestPostsPage;
